<template>
  <header class="app-header">
    <div class="header-inner">
      <!-- Brand -->
      <button class="brand" @click="navigateHome">
        <div class="brand-mark">AB</div>
        <div class="brand-info">
          <span class="brand-name">ABPoint</span>
          <span class="brand-sub">Gestion RH</span>
        </div>
      </button>

      <!-- Center Badge -->
      <div class="header-center">
        <div v-if="privilage()" class="admin-chip">
          <span class="chip-dot"></span>
          {{ roleLabel }}
        </div>
      </div>

      <!-- Actions -->
      <div class="header-actions">
        <button v-if="showRoleBackButton" class="hbtn hbtn-back" @click="goRoleHome" title="Retour">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
            <path d="M19 12H5" />
            <path d="m12 19-7-7 7-7" />
          </svg>
        </button>
        <div v-if="privilage() == 'admin'" class="notification-wrap">
          <button class="hbtn notification-btn" @click="toggleNotifications" title="Notifications">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 8a6 6 0 10-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>
            <span v-if="pendingCount" class="notification-badge">{{ pendingCount }}</span>
          </button>
          <div v-if="showNotifications" class="notification-panel">
            <div class="notification-head">
              <div>
                <strong>Notifications</strong>
                <span>{{ pendingCount }} nouvelle{{ pendingCount > 1 ? 's' : '' }}</span>
              </div>
              <button @click="goNotifications">Voir toutes</button>
            </div>
            <div v-if="notificationsLoading" class="notification-empty">Chargement...</div>
            <div v-else-if="!notifications.length" class="notification-empty">Aucune nouvelle notification.</div>
            <div v-else class="notification-list">
              <article v-for="item in displayedNotifications" :key="item.id" class="notification-item" :class="item.severity">
                <div>
                  <strong>{{ item.title }}</strong>
                  <span>{{ item.first_name }} {{ item.last_name }} - {{ formatDate(item.work_date) }}</span>
                  <p>{{ item.message }}</p>
                </div>
                <div class="notification-actions">
                  <button @click="openNotificationTarget(item)">
                    {{ item.notification_type === 'cashier_advance' ? 'Voir avances' : 'Voir pointage' }}
                  </button>
                </div>
              </article>
            </div>
          </div>
        </div>
        <button v-if="privilage()" class="hbtn" @click="navigate2('/pointage/myprofile')" title="Profil">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
        </button>

        <button class="hbtn hbtn-danger" @click="logout" title="Déconnexion">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/></svg>
        </button>
      </div>
    </div>
  </header>
</template>

<script>
export default {
  inject: ['privilage'],
  name: 'AppHeader',
  data() {
    return {
      showNotifications: false,
      notifications: [],
      notificationsLoading: false,
      notificationsTimer: null,
    };
  },
  computed: {
    roleLabel() {
      const labels = {
        admin: 'Administrateur',
        caissier: 'Caissier',
        employe: 'Employé',
      };
      return labels[this.privilage()] || this.privilage();
    },
    pendingCount() {
      return this.notifications.filter(item => item.status === 'pending').length;
    },
    displayedNotifications() {
      return this.notifications.slice(0, 5);
    },
    showRoleBackButton() {
      const role = this.privilage();
      return (role === 'employe' || role === 'caissier') && this.$route.path !== this.homePathForRole(role);
    },
  },
  mounted() {
    if (this.privilage() === 'admin') {
      this.fetchNotifications();
      this.notificationsTimer = setInterval(() => {
        this.fetchNotifications({ silent: true });
      }, 30000);
    }
  },
  beforeUnmount() {
    if (this.notificationsTimer) {
      clearInterval(this.notificationsTimer);
      this.notificationsTimer = null;
    }
  },
  methods: {
    toggleNotifications() {
      this.showNotifications = !this.showNotifications;
      if (this.showNotifications) this.fetchNotifications();
    },
    async fetchNotifications(options = {}) {
      if (this.privilage() !== 'admin') return;
      if (!options.silent) this.notificationsLoading = true;
      try {
        const res = await fetch('/api-pointage/notifications?status=pending&limit=300', {
          credentials: 'include',
          cache: 'no-store',
        });
        const contentType = res.headers.get('content-type') || '';
        if (!res.ok || !contentType.includes('application/json')) {
          this.notifications = [];
          return;
        }
        const data = await res.json();
        this.notifications = Array.isArray(data) ? data : [];
      } catch (err) {
        console.error('Erreur notifications:', err);
        this.notifications = [];
      } finally {
        if (!options.silent) this.notificationsLoading = false;
      }
    },
    async markSeen(item) {
      if (!item?.id || item.status === 'validated') return;
      await fetch('/api-pointage/notifications/seen', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.id }),
      });
    },
    goNotifications() {
      this.showNotifications = false;
      this.$router.push('/notifications');
    },
    async openNotificationTarget(item) {
      await this.markSeen(item);
      this.showNotifications = false;
      if (item.notification_type === 'cashier_advance') {
        this.$router.push({
          path: '/avances',
          query: { staff_id: item.staff_id, date: item.work_date },
        });
        return;
      }
      this.$router.push({
        path: '/pointage',
        query: { staff_id: item.staff_id, date: item.work_date },
      });
    },
    formatDate(value) {
      if (!value) return '';
      const [year, month, day] = String(value).split('-');
      return `${day}/${month}/${year}`;
    },
    logout() {
      document.cookie = "session_id=; Max-Age=0; path=/";
      window.dispatchEvent(new Event('auth-changed'));
      this.$router.replace('/login');
    },
    navigate2(path) {
      this.$router.push(path.replace(/^\/pointage/, '') || '/');
    },
    navigateHome() {
      const role = this.privilage();
      this.$router.push(this.homePathForRole(role));
    },
    goRoleHome() {
      this.$router.push(this.homePathForRole(this.privilage()));
    },
    homePathForRole(role) {
      if (role === 'caissier') return '/cashier';
      if (role === 'employe') return '/mes-avances';
      return '/';
    }
  }
}
</script>

<style>
.app-header {
  background: var(--white);
  border-bottom: 1px solid var(--border);
  height: var(--header-height);
  position: sticky;
  top: 0;
  z-index: 1500;
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.04);
  transition: background var(--transition), border-color var(--transition);
}

.header-inner {
  height: 100%;
  padding: 0 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px 8px;
  border-radius: var(--radius-sm);
  transition: background var(--transition);
  min-width: 0;
  flex-shrink: 0;
}
.brand:hover { background: var(--gray-100); }

.brand-mark {
  width: 34px; height: 34px;
  background: var(--primary);
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  font-family: 'Arial', sans-serif;
  font-size: 13px; font-weight: 800;
  color: #fff;
  box-shadow: none;
  flex-shrink: 0;
}

.brand-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.brand-name {
  font-family: 'Arial', sans-serif;
  font-size: 14px; font-weight: 800;
  color: var(--gray-900);
  letter-spacing: 0.02em;
  white-space: nowrap;
  transition: color var(--transition);
}

.brand-sub {
  font-size: 11px; font-weight: 600;
  color: var(--gray-500);
  letter-spacing: 0.02em;
}

.header-center {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.admin-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 4px 12px;
  background: #f8fafc;
  border: 1px solid var(--border);
  border-radius: var(--radius-full);
  font-size: 12px; font-weight: 700;
  color: var(--primary);
  letter-spacing: 0.03em;
}

.chip-dot {
  width: 6px; height: 6px;
  background: var(--primary);
  border-radius: 50%;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.hbtn {
  width: 36px; height: 36px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background: var(--white);
  color: var(--gray-500);
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all var(--transition);
  flex-shrink: 0;
}
.hbtn svg { width: 16px; height: 16px; }
.hbtn:hover {
  background: var(--gray-100);
  color: var(--gray-800);
  border-color: var(--border-2);
}

.hbtn-danger { }
.hbtn-back {
  color: var(--gray-700);
}
.hbtn-danger:hover {
  background: var(--danger-bg);
  color: var(--danger);
  border-color: rgba(239,68,68,0.25);
}

.notification-wrap {
  position: relative;
}

.notification-btn {
  position: relative;
}

.notification-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #ef4444;
  color: #fff;
  font-size: 11px;
  font-weight: 900;
  line-height: 18px;
}

.notification-panel {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: min(420px, calc(100vw - 24px));
  max-height: min(520px, calc(100vh - var(--header-height) - 24px));
  overflow: hidden;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.18);
}

.notification-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 52px;
  padding: 0 14px;
  border-bottom: 1px solid var(--border);
}

.notification-head > div {
  display: grid;
  gap: 2px;
}

.notification-head strong {
  color: var(--gray-900);
  font-size: 14px;
}

.notification-head span {
  color: var(--gray-500);
  font-size: 12px;
  font-weight: 800;
}

.notification-head button,
.notification-actions button {
  min-height: 30px;
  border: 1px solid var(--border);
  border-radius: 7px;
  background: #fff;
  color: var(--gray-700);
  padding: 0 10px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}

.notification-list {
  display: grid;
  gap: 8px;
  max-height: 455px;
  overflow-y: auto;
  padding: 10px;
}

.notification-empty {
  padding: 24px 14px;
  color: var(--gray-500);
  text-align: center;
  font-weight: 700;
}

.notification-item {
  display: grid;
  gap: 10px;
  padding: 12px;
  border: 1px solid #dbe3f1;
  border-left: 4px solid #2563eb;
  border-radius: 8px;
  background: #f8fbff;
}

.notification-item.high {
  border-left-color: #ef4444;
}

.notification-item strong,
.notification-item span,
.notification-item p {
  display: block;
}

.notification-item strong {
  color: var(--gray-900);
  font-size: 14px;
}

.notification-item span {
  margin-top: 3px;
  color: var(--gray-500);
  font-size: 12px;
  font-weight: 800;
}

.notification-item p {
  margin: 7px 0 0;
  color: var(--gray-700);
  font-size: 13px;
  line-height: 1.4;
}

.notification-actions {
  display: flex;
  justify-content: flex-end;
  gap: 7px;
}

.notification-actions button:last-child {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}

@media (max-width: 640px) {
  .app-header {
    height: var(--header-height);
  }
  .header-inner { padding: 0 12px; gap: 10px; }
  .brand-name { font-size: 13px; }
  .brand-sub { display: none; }
  .admin-chip { display: none; }
  .brand-mark {
    width: 34px;
    height: 34px;
  }
  .hbtn {
    width: 42px;
    height: 42px;
  }
  .hbtn svg {
    width: 17px;
    height: 17px;
  }
  .notification-panel {
    position: fixed;
    top: var(--header-height);
    right: 0;
    left: 0;
    width: auto;
    max-height: calc(100vh - var(--header-height));
    border-radius: 0 0 12px 12px;
    border-left: 0;
    border-right: 0;
    box-shadow: 0 18px 42px rgba(15, 23, 42, 0.2);
    z-index: 1501;
  }

  .notification-head {
    min-height: 48px;
    padding: 0 12px;
  }

  .notification-list {
    max-height: calc(100vh - var(--header-height) - 58px);
    gap: 6px;
    padding: 8px;
  }

  .notification-item {
    grid-template-columns: 1fr;
    gap: 8px;
    padding: 10px;
  }

  .notification-item strong {
    font-size: 13px;
  }

  .notification-item p {
    margin-top: 5px;
    font-size: 12px;
    line-height: 1.35;
  }

  .notification-actions button {
    width: 100%;
    min-height: 34px;
  }
}

@media (max-width: 420px) {
  .header-inner {
    padding: 0 10px;
    gap: 8px;
  }

  .brand {
    gap: 9px;
    padding-left: 4px;
    padding-right: 4px;
  }

  .brand-mark {
    width: 34px;
    height: 34px;
    border-radius: 9px;
  }

  .header-actions {
    gap: 5px;
  }

  .hbtn { width: 40px; height: 40px; }
}
</style>
